import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * useReviewerCampaign
 *
 * Fetches the live reviewer payload for a single shared campaign:
 * allow-listed contact fields (name / title / location) plus per-draft
 * subject/body and compliance decisions with a server-computed
 * `is_stale` flag. Backs `ReviewerCampaignPage` and downstream
 * components.
 *
 * Cache shape mirrors `useSharedCampaigns`:
 *   - user-scoped key (auto-invalidated on logout via
 *     `AuthContext.queryClient.clear()`)
 *   - 60s staleTime — reviewers rarely need sub-minute freshness, and
 *     React Query still refetches on window focus so an open tab
 *     catches up when the advisor iterates.
 *
 * Disabled until both a user and a campaignId are present so we don't
 * spam the API during logout transitions or page redirects.
 */
export function useReviewerCampaign(campaignId) {
  const { user } = useAuth();

  const queryKey = user?.id && campaignId
    ? ['reviewerCampaign', user.id, campaignId]
    : ['reviewerCampaign', 'disabled'];

  return useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await api.get(`/api/reviews/campaigns/${campaignId}`);
      return data;
    },
    enabled: Boolean(user?.id && campaignId),
    staleTime: 60 * 1000,
    retry: (failureCount, err) => {
      // Don't retry a clean 404/403 — the share is gone or the user
      // isn't authorized. Letting React Query hammer makes the error
      // UI flicker for no benefit.
      const statusCode = err?.response?.status;
      if (statusCode === 404 || statusCode === 403) return false;
      return failureCount < 1;
    },
  });
}
